import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useShop } from "@/contexts/ShopContext";
import { languages } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV, AUTH } from "@/constants/testIds";
import { Moon, Sun, LogOut, Globe } from "lucide-react";

export default function TopBar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { shops, selectedShopId, setSelectedShopId, selectedShop } = useShop();
  const [theme, setTheme] = useState(() => localStorage.getItem("rk-theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("rk-theme", theme);
  }, [theme]);

  return (
    <header className="h-16 flex items-center gap-3 border-b border-border px-6 lg:px-10 bg-background">
      <div className="flex items-center gap-3 min-w-0">
        <Select value={selectedShopId ?? undefined} onValueChange={setSelectedShopId}>
          <SelectTrigger
            data-testid={NAV.shopSelector}
            className="w-64 h-10 font-mono text-xs"
          >
            <SelectValue placeholder={t("shop")}>
              {selectedShop ? (
                <span className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: `hsl(var(--chart-${(shops.findIndex(s=>s.shop_id===selectedShopId)%5)+1}))` }}
                  />
                  <span className="truncate">{selectedShop.domain}</span>
                </span>
              ) : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {shops.map((s, i) => (
              <SelectItem
                key={s.shop_id}
                value={s.shop_id}
                className="font-mono text-xs"
                data-testid={`shop-option-${s.shop_id}`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: `hsl(var(--chart-${(i%5)+1}))` }}
                  />
                  <span>{s.domain}</span>
                  <span className="text-muted-foreground">· {s.industry}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger
            data-testid={NAV.languageSwitcher}
            className="w-28 h-9 font-mono text-xs"
          >
            <Globe className="h-3.5 w-3.5 mr-1.5" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((l) => (
              <SelectItem key={l.code} value={l.code} className="font-mono text-xs">
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          data-testid={NAV.themeToggle}
          className="h-9 w-9"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-9 w-9 rounded-full overflow-hidden border border-border grid place-items-center bg-secondary text-xs font-mono"
              data-testid="user-menu-btn"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() ?? "U"
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
              <div className="text-sm">{user?.name}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="text-destructive focus:text-destructive gap-2"
            >
              <LogOut className="h-4 w-4" /> {t("logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
